import { useEffect, useRef, useState, useCallback } from 'react';
import type { Canvas, WebSocketMessage } from '../types';

export function useCanvasWebSocket() {
  const [canvas, setCanvas] = useState<Canvas | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout>();
  const pingIntervalRef = useRef<NodeJS.Timeout>();

  const connect = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      return;
    }

    try {
      // Determine WebSocket URL based on current location
      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const wsUrl = `${protocol}//${window.location.host}/api/ws`;
      const ws = new WebSocket(wsUrl);

      ws.onopen = () => {
        console.log('WebSocket connected');
        setIsConnected(true);

        // Start ping interval to keep connection alive
        pingIntervalRef.current = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ type: 'ping' } as WebSocketMessage));
          }
        }, 30000); // Ping every 30 seconds
      };

      ws.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data) as WebSocketMessage;

          if (message.type === 'full_canvas') {
            setCanvas(message.data as Canvas);
          } else if (message.type === 'canvas_update') {
            const { updates } = message.data;
            setCanvas((prev) => {
              if (!prev) return prev;
              
              // Only copy the affected rows to minimize memory allocation
              const newPixels = [...prev.pixels];
              const affectedRows = new Set<number>();
              
              for (const update of updates) {
                if (update.x >= 0 && update.x < 100 && update.y >= 0 && update.y < 100) {
                  affectedRows.add(update.y);
                }
              }
              
              // Only copy rows that will be modified
              affectedRows.forEach(y => {
                newPixels[y] = [...prev.pixels[y]];
              });
              
              // Apply updates
              for (const update of updates) {
                if (update.x >= 0 && update.x < 100 && update.y >= 0 && update.y < 100) {
                  newPixels[update.y][update.x] = update.color;
                }
              }
              
              return { ...prev, pixels: newPixels, lastUpdated: Date.now() };
            });
          }
        } catch (error) {
          console.error('Error parsing WebSocket message:', error);
        }
      };

      ws.onerror = (error) => {
        console.error('WebSocket error:', error);
      };

      ws.onclose = () => {
        console.log('WebSocket disconnected');
        setIsConnected(false);
        if (pingIntervalRef.current) {
          clearInterval(pingIntervalRef.current);
        }

        // Attempt to reconnect after 3 seconds
        reconnectTimeoutRef.current = setTimeout(() => {
          console.log('Attempting to reconnect...');
          connect();
        }, 3000);
      };

      wsRef.current = ws;
    } catch (error) {
      console.error('Failed to connect WebSocket:', error);
    }
  }, []);

  useEffect(() => {
    connect();

    return () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (pingIntervalRef.current) {
        clearInterval(pingIntervalRef.current);
      }
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, [connect]);

  return { canvas, isConnected };
}

